import { useState, useEffect, useRef } from 'react';
import Input from '../base/Input';
import { getSuggestions, type Coordinates } from '../../services/geocoding';

interface LocationAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    icon?: string;
    className?: string;
}

export default function LocationAutocomplete({
    value,
    onChange,
    placeholder,
    icon,
    className
}: LocationAutocompleteProps) {
    const [suggestions, setSuggestions] = useState<Coordinates[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const fetchSuggestions = async () => {
            if (value.length > 2) {
                const results = await getSuggestions(value);
                setSuggestions(results);
                setShowSuggestions(true);
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        };

        const timeoutId = setTimeout(fetchSuggestions, 500); // Debounce
        return () => clearTimeout(timeoutId);
    }, [value]);

    const handleSelect = (suggestion: Coordinates) => {
        onChange(suggestion.displayName);
        setShowSuggestions(false);
    };

    return (
        <div ref={wrapperRef} className="relative w-full">
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                icon={icon}
                className={className}
                onFocus={() => value.length > 2 && setShowSuggestions(true)}
            />

            {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 border-b border-gray-100 last:border-0"
                            onClick={() => handleSelect(suggestion)}
                        >
                            <div className="flex items-center">
                                <i className="ri-map-pin-2-line text-gray-400 mr-2"></i>
                                <span>{suggestion.displayName}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
